import React, { useCallback, useEffect, useState } from "react";
import {
  updateProfilePage,
  fetchUserProfile,
} from "../../services/newUserService";
import allImages from "../../assets/images-import";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { validateProfileForm } from "../../utils/validation";
import { handleErrors } from "../../utils/errorHandler";
import { toast } from "react-toastify";
import {
  isValidProfileImageSrc,
  isRejectedProfileImageValue,
  validateProfileImageFile,
  PROFILE_IMAGE_WIDTH,
  PROFILE_IMAGE_HEIGHT,
} from "../../utils/profileImageHelper";

export const ProfilePageContent = ({ onBack }) => {
  const [ownerUserId, setUserId] = useState("");
  const [ownerUserName, setUserName] = useState("");
  const [ownerUserRole, setOwnerUserRole] = useState("");
  const [ownerUserRoleId, setOwnerUserRoleId] = useState("");
  const [ownerEmailId, setOwnerEmailId] = useState("");
  const [ownerContactNo, setOwnerContactNo] = useState("");
  const [ownerDOJ, setOwnerDOJ] = useState("");
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [currentProfileImage, setCurrentProfileImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const formatDateForInput = useCallback((dateStr) => {
    if (!dateStr) return "";
    return dateStr.includes("T") ? dateStr.split("T")[0] : dateStr.slice(0, 10);
  }, []);

  const applyProfileData = useCallback((profileData) => {
    setUserId(profileData.userId);
    setUserName(profileData.userName);
    setOwnerUserRoleId(profileData.userRole);
    setOwnerUserRole(profileData.roleName);
    setOwnerEmailId(profileData.emailId);
    setOwnerContactNo(profileData.contactNo);
    setOwnerDOJ(formatDateForInput(profileData.doj));
    setCurrentProfileImage(
      isValidProfileImageSrc(profileData.profileImage)
        ? profileData.profileImage
        : null
    );
  }, [formatDateForInput]);

  const loadProfile = useCallback(async () => {
    const amenityData = await fetchUserProfile();
    const profileData = amenityData.data.result;
    applyProfileData(profileData);
    return profileData;
  }, [applyProfileData]);

  useEffect(() => {
    const fetchOwnerData = async () => {
      try {
        await loadProfile();
      } catch (error) {
        handleErrors(error);
      }
    };

    fetchOwnerData();
  }, [loadProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateProfileForm(
      ownerUserId,
      ownerUserName,
      ownerUserRole,
      ownerEmailId,
      ownerContactNo,
      ownerDOJ
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("UserId", ownerUserId);
      formData.append("UserName", ownerUserName);
      formData.append("UserRole", String(ownerUserRoleId));
      formData.append("EmailId", ownerEmailId);
      formData.append("ContactNo", ownerContactNo);
      formData.append("DOJ", ownerDOJ);

      if (ownerProfile) {
        formData.append("ProfileImage", ownerProfile, ownerProfile.name);
      }
      const uploadedNewImage = Boolean(ownerProfile);
      setIsButtonDisabled(true);
      const response = await updateProfilePage(formData);

      if (response?.isSuccess === false) {
        toast.error(response.message || "Profile update failed.");
        setIsButtonDisabled(false);
        return;
      }

      const savedProfile = await loadProfile();

      if (uploadedNewImage) {
        const savedImage = savedProfile?.profileImage;

        if (
          isRejectedProfileImageValue(savedImage) ||
          !isValidProfileImageSrc(savedImage)
        ) {
          toast.error(
            response?.message ||
              `Profile details were saved, but the image was not accepted by the server. Please upload a ${PROFILE_IMAGE_WIDTH}x${PROFILE_IMAGE_HEIGHT} pixel JPG, PNG, or WEBP file.`
          );
          setIsButtonDisabled(false);
          return;
        }

        setOwnerProfile(null);
        toast.success("Profile and image updated successfully.");
        setIsButtonDisabled(false);
        return;
      }

      toast.success(response?.message || "Profile Updated Successfully");
      setIsButtonDisabled(false);
    } catch (error) {
      handleErrors(error);
      setIsButtonDisabled(false);
    }
  };

  const handleProfileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setOwnerProfile(null);
      return;
    }

    try {
      await validateProfileImageFile(file);
      setErrors((prev) => ({ ...prev, profileImage: "" }));
      setOwnerProfile(file);
      setCurrentProfileImage(URL.createObjectURL(file));
    } catch (error) {
      setOwnerProfile(null);
      e.target.value = "";
      setErrors((prev) => ({
        ...prev,
        profileImage: error.message,
      }));
      toast.error(error.message);
    }
  };

  const handlePhoneChange = (value) => {
    setOwnerContactNo(value);
  };

  return (
    <>
      <ComponentHeader title="Profile Page" />
      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="text-center">
                      <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                        {currentProfileImage ? (
                          <img
                            src={currentProfileImage}
                            className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                            alt="user-profile-image"
                          />
                        ) : (
                          <img
                            src={allImages.defaultprofile}
                            className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                            alt="default-profile-image"
                          />
                        )}
                        <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                          <input
                            id="profileImage"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="profile-img-file-input"
                            onChange={handleProfileChange}
                          />
                          <label
                            htmlFor="profileImage"
                            className="profile-photo-edit avatar-xs"
                          >
                            <span className="avatar-title rounded-circle bg-light text-body shadow">
                              <i className="ri-camera-fill"></i>
                            </span>
                          </label>
                        </div>
                      </div>
                      <p className="text-muted small mb-0">
                        Upload JPG, PNG, or WEBP. Required size:{" "}
                        {PROFILE_IMAGE_WIDTH}x{PROFILE_IMAGE_HEIGHT} pixels.
                      </p>
                      {errors.profileImage && (
                        <small className="text-danger d-block mt-2">
                          {errors.profileImage}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label htmlFor="userid" className="form-label">
                            User Id <span className="required-field">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={ownerUserId}
                            onChange={(e) => setUserId(e.target.value)}
                          />
                          {errors.userId && (
                            <small className="text-danger">
                              {errors.userId}
                            </small>
                          )}{" "}
                          {/* Show error */}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="owner_firstName"
                            className="form-label"
                          >
                            User Name <span className="required-field">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={ownerUserName}
                            onChange={(e) => setUserName(e.target.value)}
                          />
                          {errors.userName && (
                            <small className="text-danger">
                              {errors.userName}
                            </small>
                          )}{" "}
                          {/* Show error */}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label htmlFor="UserRole" className="form-label">
                            User Role <span className="required-field">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={ownerUserRole}
                            disabled
                          />
                          {errors.userRole && (
                            <small className="text-danger">
                              {errors.userRole}
                            </small>
                          )}{" "}
                          {/* Show error */}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label htmlFor="EmailId" className="form-label">
                            Email Address{" "}
                            <span className="required-field">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={ownerEmailId}
                            onChange={(e) => setOwnerEmailId(e.target.value)}
                          />
                          {errors.emailId && (
                            <small className="text-danger">
                              {errors.emailId}
                            </small>
                          )}{" "}
                          {/* Show error */}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label htmlFor="contactNo" className="form-label">
                            Contact No <span className="required-field">*</span>
                          </label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            value={ownerContactNo}
                            maxLength="15"
                            onChange={handlePhoneChange}
                          />
                          {errors.contactNo && (
                            <small className="text-danger">
                              {errors.contactNo}
                            </small>
                          )}{" "}
                          {/* Show error */}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label htmlFor="doj" className="form-label">
                            Date Of Joining{" "}
                            <span className="required-field">*</span>
                          </label>
                          <input
                            type="date"
                            id="doj"
                            className="form-control"
                            value={ownerDOJ}
                            onChange={(e) => setOwnerDOJ(e.target.value)}
                          />
                          {errors.doj && (
                            <small className="text-danger">{errors.doj}</small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-start mt-4">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled}
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
